import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    useAccessFeatures,
    canUseFeature,
} from '../../../auth/useAccessFeatures';
import { activeSectionId, type NavSection } from '../navSections';
import { useMenuEntries } from '../useMenuEntries';
import { MobileTopBar } from './MobileTopBar';
import { MobileSearchDock } from './MobileSearchDock';
import { MoreSheet } from './MoreSheet';

interface MobileShellProps {
    sections: NavSection[];
    showTabBar: boolean;
    showSearchRow: boolean;
    /** Out of the way while the user types in a field of the page. */
    dockHidden?: boolean;
}

// Everything around the page below 900 px. Replaces Header there.
export function MobileShell({
    sections,
    showTabBar,
    showSearchRow,
    dockHidden = false,
}: MobileShellProps) {
    const { pathname } = useLocation();
    const [moreOpen, setMoreOpen] = useState(false);
    // The sheet is on screen: from the moment it opens until it has slid out
    // (it is still there for ~195 ms after moreOpen goes false, and the dock
    // has to stay above it that long).
    const [sheetShown, setSheetShown] = useState(false);
    useLayoutEffect(() => {
        if (moreOpen) setSheetShown(true);
    }, [moreOpen]);
    const sheetGone = useCallback(() => setSheetShown(false), []);
    const access = useAccessFeatures();
    const entries = useMenuEntries();

    // Navigating anywhere closes the sheet.
    useEffect(() => setMoreOpen(false), [pathname]);

    // Stable: the sheet must not see a new function on every render of the
    // shell (a theme switch, an identity refetch) as a reason to do anything.
    const openMore = useCallback(() => setMoreOpen(true), []);
    const toggleMore = useCallback(() => setMoreOpen(open => !open), []);
    const closeMore = useCallback(() => setMoreOpen(false), []);

    return (
        <>
            <MobileTopBar
                showMore={!showTabBar}
                moreOpen={moreOpen}
                onMore={openMore}
            />
            <MobileSearchDock
                sections={sections}
                activeId={activeSectionId(sections, pathname)}
                showTabBar={showTabBar}
                showSearchRow={showSearchRow}
                hidden={dockHidden}
                moreOpen={moreOpen}
                sheetShown={sheetShown}
                // The tab bar's More is Close while the sheet is open.
                onMore={toggleMore}
                // Same gate as the header's advanced-search icon (ROUTE_ACCESS: events).
                advancedHref={
                    canUseFeature(access, 'events')
                        ? '/markets/search'
                        : undefined
                }
            />
            <MoreSheet
                open={moreOpen}
                onClose={closeMore}
                entries={entries}
                aboveTabBar={showTabBar}
                onExited={sheetGone}
            />
        </>
    );
}
